import { useEffect, useMemo, useState, useRef } from 'react';
import api from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import { crc, fecha, fechaHora, METODOS_PAGO, LEYENDA_IVA_INCLUIDO } from '../utils/format.js';
import { PageHeader, Modal, Loading, EmptyState } from '../components/UI.jsx';
import { Icon } from '../components/Icons.jsx';
import FilterBar from '../components/FilterBar.jsx';
import ImagenProtegida from '../components/ImagenProtegida.jsx';
import TiqueteReparacion from '../components/TiqueteReparacion.jsx';

// Estados del taller, con color de badge. "reparando" y "diagnostico"
// comparten familia (morado) pero con tono distinto para diferenciarse
// de un vistazo sin salirse de la paleta del sistema.
const ESTADOS = [
  { value: 'recibido', label: 'Recibido', bg: 'var(--info-bg)', color: 'var(--info)' },
  { value: 'diagnostico', label: 'Diagnostico', bg: 'var(--accent-soft)', color: 'var(--accent-texto)' },
  { value: 'esperando_repuesto', label: 'Esperando repuesto', bg: 'var(--warning-bg)', color: 'var(--warning)' },
  { value: 'reparando', label: 'Reparando', bg: 'rgba(139, 92, 246, 0.16)', color: 'var(--purple-400)' },
  { value: 'listo', label: 'Listo', bg: 'var(--success-bg)', color: 'var(--success)' },
  { value: 'entregado', label: 'Entregado', bg: 'var(--surface-2)', color: 'var(--text-muted)' },
  { value: 'no_reparable', label: 'No reparable', bg: 'var(--danger-bg)', color: 'var(--danger)' },
];
const ESTADO_INFO = Object.fromEntries(ESTADOS.map((e) => [e.value, e]));
const CERRADAS = ['entregado', 'no_reparable'];

// Suma de extras: la lista (GET /reparaciones) trae el agregado ya calculado
// en "total_extras"; el detalle (GET /reparaciones/:id) trae el arreglo
// "extras" completo. Se soportan ambas formas.
const sumaExtras = (r) => Array.isArray(r.extras)
  ? r.extras.reduce((a, e) => a + Number(e.monto || 0), 0)
  : Number(r.total_extras || 0);
const totalReal = (r) => Number(r.total || 0) + sumaExtras(r);
const pendiente = (r) => Math.max(totalReal(r) - Number(r.abono || 0), 0);
const estaPagada = (r) => totalReal(r) > 0 && pendiente(r) <= 0.001;

function EstadoBadge({ estado }) {
  const info = ESTADO_INFO[estado] || { label: estado, bg: 'var(--accent-soft)', color: 'var(--accent-texto)' };
  return <span className="badge" style={{ background: info.bg, color: info.color }}>{info.label}</span>;
}

function PagoBadge({ reparacion }) {
  const pagado = estaPagada(reparacion);
  return (
    <span
      className="badge"
      style={pagado
        ? { background: 'var(--success-bg)', color: 'var(--success)' }
        : { background: 'var(--danger-bg)', color: 'var(--danger)' }}
    >
      {pagado ? 'Pagado' : 'Pendiente'}
    </span>
  );
}

// Una cifra de la cuenta de la reparacion.
function Cifra({ etiqueta, valor, color, fuerte }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 12 }}>{etiqueta}</div>
      <div className="mono" style={{ fontWeight: fuerte ? 800 : 600, fontSize: fuerte ? 17 : 15, color: color || 'var(--text)' }}>
        {crc(valor)}
      </div>
    </div>
  );
}

// Encabezado de un bloque del detalle. Solo el titulo: sin rotulo encima.
function Seccion({ titulo, derecha, children }) {
  return (
    <section style={{ marginTop: 22 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0, fontSize: 15 }}>{titulo}</h4>
        {derecha}
      </div>
      {children}
    </section>
  );
}

// Ancho maximo al que se reduce una foto antes de subirla. Una foto de
// celular pesa varios MB y para documentar un equipo no hace falta tanto:
// asi el disco del servidor no se llena en un mes.
const ANCHO_FOTO = 1600;

/** Reduce la foto manteniendo su tipo (PNG sigue PNG, JPG sigue JPG). */
function encogerFoto(archivo) {
  return new Promise((resolve) => {
    if (archivo.size < 400 * 1024) return resolve(archivo); // ya es chica
    const lector = new FileReader();
    lector.onerror = () => resolve(archivo);
    lector.onload = () => {
      const img = new Image();
      img.onerror = () => resolve(archivo);
      img.onload = () => {
        if (img.width <= ANCHO_FOTO) return resolve(archivo);
        const escala = ANCHO_FOTO / img.width;
        const lienzo = document.createElement('canvas');
        lienzo.width = Math.round(img.width * escala);
        lienzo.height = Math.round(img.height * escala);
        lienzo.getContext('2d').drawImage(img, 0, 0, lienzo.width, lienzo.height);
        lienzo.toBlob(
          (blob) => resolve(blob ? new File([blob], archivo.name, { type: archivo.type }) : archivo),
          archivo.type,
          0.85,
        );
      };
      img.src = lector.result;
    };
    lector.readAsDataURL(archivo);
  });
}

// Mosaico de fotos grande. En el detalle se ven para que sirvan: con 78px
// no se distinguia un vidrio quebrado de un reflejo.
const MOSAICO = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: 10,
};
const BOTON_BORRAR_FOTO = {
  position: 'absolute', top: 6, right: 6, padding: 0,
  width: 26, height: 26, borderRadius: '50%',
  background: 'rgba(0, 0, 0, 0.62)', color: '#fff', border: 0, lineHeight: 1,
};

export default function Reparaciones() {
  const toast = useToast();
  const [reparaciones, setReparaciones] = useState(undefined);
  const [errorLista, setErrorLista] = useState('');
  const [clientes, setClientes] = useState([]);

  const [q, setQ] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  // Fecha de entrada al taller. Vacio por defecto: un equipo que entro hace
  // dos meses y sigue en el taller no puede desaparecer de la lista.
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [cargandoEditar, setCargandoEditar] = useState(false);
  const [form, setForm] = useState({});
  const [guardando, setGuardando] = useState(false);

  const [nuevoExtra, setNuevoExtra] = useState({ descripcion: '', monto: '' });
  const [agregandoExtra, setAgregandoExtra] = useState(false);
  const [nuevoAbono, setNuevoAbono] = useState({ monto: '', metodo: 'efectivo', nota: '' });
  const [agregandoAbono, setAgregandoAbono] = useState(false);

  // Alta de cliente sin salir de la reparacion. En el taller el equipo entra
  // con el cliente enfrente: mandarlo a otra pantalla a registrarlo y volver
  // es perder la reparacion a medio llenar.
  const [formCliente, setFormCliente] = useState(null);
  const [creandoCliente, setCreandoCliente] = useState(false);

  // Fotos del equipo. Cuando la reparacion todavia no existe se guardan
  // aca y se suben apenas se crea: el equipo se fotografia al recibirlo,
  // no despues.
  const [pendientes, setPendientes] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const archivoRef = useRef();
  const [verFoto, setVerFoto] = useState(null);
  const [tiquete, setTiquete] = useState(null);

  const cargar = () => {
    setErrorLista('');
    const params = {};
    if (q) params.q = q;
    if (filtroEstado) params.estado = filtroEstado;
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    api.get('/reparaciones', { params })
      .then((r) => setReparaciones(r.data.data || []))
      .catch((err) => {
        setReparaciones(null);
        setErrorLista(err.response?.data?.message || 'No se pudo cargar el listado de reparaciones');
      });
  };
  useEffect(cargar, [q, filtroEstado, desde, hasta]);

  useEffect(() => {
    api.get('/clientes').then((r) => setClientes(r.data.data || [])).catch(() => {});
  }, []);

  // Lo que hay que cobrar, a la vista. Sale de la misma lista que se esta
  // viendo: si se filtra, los numeros acompañan al filtro.
  const resumen = useMemo(() => {
    const lista = reparaciones || [];
    return {
      porCobrar: lista.reduce((s, r) => s + pendiente(r), 0),
      conSaldo: lista.filter((r) => pendiente(r) > 0.001).length,
      abiertas: lista.filter((r) => !CERRADAS.includes(r.estado)).length,
      listas: lista.filter((r) => r.estado === 'listo').length,
    };
  }, [reparaciones]);

  const CLIENTE_NUEVO = { nombre: '', telefono: '', email: '', identificacion: '' };

  // Crea el cliente y lo deja seleccionado en la reparacion.
  const crearCliente = async () => {
    const nombre = (formCliente?.nombre || '').trim();
    if (!nombre) {
      toast.error('El nombre del cliente es requerido');
      return;
    }
    setCreandoCliente(true);
    try {
      const { data } = await api.post('/clientes', {
        ...formCliente,
        nombre,
        identificacion: String(formCliente.identificacion || '').replace(/\D/g, ''),
      });
      const cliente = data.data;
      setClientes((prev) =>
        [...prev.filter((c) => c.id !== cliente.id), cliente]
          .sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es')),
      );
      setForm((f) => ({ ...f, cliente_id: String(cliente.id) }));
      setFormCliente(null);
      toast.success(`Cliente "${cliente.nombre}" creado`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo crear el cliente');
    } finally {
      setCreandoCliente(false);
    }
  };

  const abrirNuevo = () => {
    setEditando(null);
    setFormCliente(null);
    setPendientes([]);
    setNuevoAbono({ monto: '', metodo: 'efectivo', nota: '' });
    setForm({
      dispositivo: '', cliente_id: '',
      descripcion: '', fecha_entrega_estimada: '',
      total: '', abono: '', metodo_abono: 'efectivo', notas: '',
    });
    setModal(true);
  };

  const abrirEditar = async (r) => {
    setEditando(r);
    setFormCliente(null);
    setPendientes([]);
    setForm({
      dispositivo: r.dispositivo || '',
      cliente_id: r.cliente_id || '',
      descripcion: r.descripcion || '',
      fecha_entrega_estimada: r.fecha_entrega_estimada ? String(r.fecha_entrega_estimada).slice(0, 10) : '',
      total: r.total ?? '',
      notas: r.notas || '',
    });
    setNuevoExtra({ descripcion: '', monto: '' });
    setNuevoAbono({ monto: '', metodo: 'efectivo', nota: '' });
    setModal(true);
    // La fila del listado no trae abonos, extras ni fotos: se busca el detalle.
    setCargandoEditar(true);
    try {
      const { data } = await api.get(`/reparaciones/${r.id}`);
      setEditando(data.data);
    } catch {
      // Si falla, se sigue trabajando con los datos que ya se tenian del listado.
    } finally {
      setCargandoEditar(false);
    }
  };

  const elegirFotos = async (lista) => {
    const archivos = Array.from(lista || []);
    if (!archivos.length) return;
    const validos = archivos.filter((a) => a.type === 'image/png' || a.type === 'image/jpeg');
    if (validos.length !== archivos.length) toast.error('Solo se aceptan imagenes PNG o JPG');
    if (!validos.length) return;

    const listos = await Promise.all(validos.map(encogerFoto));
    if (editando) await subirFotos(editando.id, listos);
    else setPendientes((prev) => [...prev, ...listos]);
    if (archivoRef.current) archivoRef.current.value = '';
  };

  const subirFotos = async (reparacionId, archivos) => {
    setSubiendo(true);
    try {
      const fd = new FormData();
      archivos.forEach((a) => fd.append('imagenes', a));
      const { data } = await api.post(`/reparaciones/${reparacionId}/imagenes`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const subidas = (Array.isArray(data.data) ? data.data : []).map((img, i) => ({
        ...img,
        preview: archivos[i] ? URL.createObjectURL(archivos[i]) : null,
      }));
      setEditando((e) => (e ? { ...e, imagenes: [...(e.imagenes || []), ...subidas], foto_id: e.foto_id || subidas[0]?.id } : e));
      toast.success(archivos.length > 1 ? 'Fotos agregadas' : 'Foto agregada');
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudieron subir las fotos');
    } finally {
      setSubiendo(false);
    }
  };

  const borrarFoto = async (imagenId) => {
    if (!confirm('Eliminar esta foto?')) return;
    try {
      await api.delete(`/reparaciones/${editando.id}/imagenes/${imagenId}`);
      setEditando((e) => ({ ...e, imagenes: (e.imagenes || []).filter((i) => i.id !== imagenId) }));
      toast.success('Foto eliminada');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo eliminar');
    }
  };

  const guardar = async () => {
    // El cliente va siempre: un equipo sin dueño no se puede avisar ni entregar.
    if (!form.cliente_id) { toast.error('Elegi el cliente o registralo con el boton +'); return; }
    if (!String(form.dispositivo || '').trim()) { toast.error('El dispositivo es requerido'); return; }

    const payload = {
      dispositivo: form.dispositivo,
      cliente_id: form.cliente_id,
      descripcion: form.descripcion,
      fecha_entrega_estimada: form.fecha_entrega_estimada || null,
      total: form.total === '' ? 0 : Number(form.total),
      notas: form.notas,
    };
    if (!editando) {
      const abono = form.abono === '' ? 0 : Number(form.abono);
      if (abono < 0) { toast.error('El abono no puede ser negativo'); return; }
      if (abono > payload.total + 0.001) {
        toast.error('El abono inicial no puede ser mayor al total');
        return;
      }
      payload.abono = abono;
      payload.metodo_abono = form.metodo_abono || 'efectivo';
    }

    setGuardando(true);
    try {
      if (editando) {
        await api.put(`/reparaciones/${editando.id}`, payload);
        toast.success('Reparacion actualizada');
        // Guardar cierra el detalle, igual que al crear, y la lista se
        // recarga abajo con los datos nuevos. Abonos, extras y fotos se
        // guardan solos al agregarlos, sin cerrar nada.
        setModal(false);
        setEditando(null);
      } else {
        const { data } = await api.post('/reparaciones', payload);
        // Las fotos que se eligieron antes de guardar se suben ahora, que
        // recien existe la reparacion a la que pertenecen.
        if (pendientes.length) {
          await subirFotos(data.data.id, pendientes);
          setPendientes([]);
        }
        toast.success(`Reparacion ${data.data.numero} creada`);
        setModal(false);
      }
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (r) => {
    if (!confirm(`Eliminar la reparacion ${r.numero || ''}?`)) return;
    try {
      await api.delete(`/reparaciones/${r.id}`);
      toast.success('Eliminada');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo eliminar');
    }
  };

  const cambiarEstado = async (r, estado) => {
    try {
      await api.patch(`/reparaciones/${r.id}/estado`, { estado });
      toast.success('Estado actualizado');
      cargar();
      if (editando?.id === r.id) setEditando((e) => ({ ...e, estado }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo cambiar el estado');
    }
  };

  const agregarExtra = async () => {
    if (!nuevoExtra.descripcion || !nuevoExtra.monto) {
      toast.error('Descripcion y monto del extra son requeridos');
      return;
    }
    setAgregandoExtra(true);
    try {
      const { data } = await api.post(`/reparaciones/${editando.id}/extras`, {
        descripcion: nuevoExtra.descripcion,
        monto: Number(nuevoExtra.monto),
      });
      const extra = data.data;
      setEditando((e) => ({ ...e, extras: [...(e.extras || []), extra] }));
      setNuevoExtra({ descripcion: '', monto: '' });
      toast.success('Extra agregado');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo agregar el extra');
    } finally {
      setAgregandoExtra(false);
    }
  };

  const agregarAbono = async () => {
    const monto = Number(nuevoAbono.monto);
    if (!(monto > 0)) {
      toast.error('Escribi de cuanto es el abono');
      return;
    }
    if (monto > pendiente(editando) + 0.001) {
      toast.error(`El abono no puede ser mayor a lo que falta (${crc(pendiente(editando))})`);
      return;
    }
    setAgregandoAbono(true);
    try {
      const { data } = await api.post(`/reparaciones/${editando.id}/abonos`, {
        monto,
        metodo: nuevoAbono.metodo,
        nota: nuevoAbono.nota,
      });
      setEditando((e) => ({
        ...e,
        abono: data.data.abono_total,
        abonos: [...(e.abonos || []), data.data.abono],
      }));
      setNuevoAbono({ monto: '', metodo: 'efectivo', nota: '' });
      toast.success(data.data.pendiente <= 0.001 ? 'Reparacion saldada' : `Abono de ${crc(monto)} registrado`);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo registrar el abono');
    } finally {
      setAgregandoAbono(false);
    }
  };

  const eliminarAbono = async (abonoId) => {
    if (!confirm('Eliminar este abono?')) return;
    try {
      await api.delete(`/reparaciones/${editando.id}/abonos/${abonoId}`);
      const quitado = (editando.abonos || []).find((a) => a.id === abonoId);
      setEditando((e) => ({
        ...e,
        abono: Math.max(0, Number(e.abono || 0) - Number(quitado?.monto || 0)),
        abonos: (e.abonos || []).filter((a) => a.id !== abonoId),
      }));
      toast.success('Abono eliminado');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo eliminar el abono');
    }
  };

  const eliminarExtra = async (extraId) => {
    if (!confirm('Eliminar este extra?')) return;
    try {
      await api.delete(`/reparaciones/extras/${extraId}`);
      setEditando((e) => ({ ...e, extras: (e.extras || []).filter((x) => x.id !== extraId) }));
      toast.success('Extra eliminado');
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo eliminar el extra');
    }
  };

  const compartir = async (r) => {
    if (!r.token_publico) {
      toast.error('Esta reparacion todavia no tiene link publico');
      return;
    }
    const link = `${window.location.origin}/reparacion/${r.token_publico}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copiado al portapapeles');
    } catch {
      toast.error('No se pudo copiar el link');
    }
  };

  const limpiarFiltros = () => {
    setQ(''); setFiltroEstado(''); setDesde(''); setHasta('');
  };

  // ---------- piezas del formulario ----------

  const campoCliente = (
    <div className="field">
      <label>Cliente *</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <select
          className="select"
          value={form.cliente_id || ''}
          onChange={(e) => setForm({ ...form, cliente_id: e.target.value })}
          style={!form.cliente_id ? { borderColor: 'var(--warning)' } : undefined}
        >
          <option value="" disabled>Elegi el cliente…</option>
          {clientes.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}{c.telefono ? ` · ${c.telefono}` : ''}</option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-outline"
          style={{ flexShrink: 0, padding: '0 12px' }}
          onClick={() => setFormCliente(formCliente ? null : CLIENTE_NUEVO)}
          title={formCliente ? 'Cancelar' : 'Registrar un cliente nuevo'}
        >
          {formCliente ? <Icon.close width={16} height={16} /> : <Icon.plus width={16} height={16} />}
        </button>
      </div>

      {/* Alta de cliente sin salir de la reparacion. Solo lo indispensable
          del taller: el resto se completa despues en Clientes. */}
      {formCliente && (
        <div style={{
          marginTop: 8, padding: 12, borderRadius: 'var(--radius-sm)',
          background: 'var(--surface-2)', border: '1px solid var(--border)',
        }}>
          <div className="grid-2" style={{ gap: 8 }}>
            <div className="field" style={{ gridColumn: '1/-1' }}>
              <label>Nombre *</label>
              <input
                className="input" autoFocus value={formCliente.nombre}
                onChange={(e) => setFormCliente({ ...formCliente, nombre: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); crearCliente(); } }}
              />
            </div>
            <div className="field">
              <label>Telefono</label>
              <input
                className="input" value={formCliente.telefono}
                onChange={(e) => setFormCliente({ ...formCliente, telefono: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); crearCliente(); } }}
              />
            </div>
            <div className="field">
              <label>Cedula</label>
              <input
                className="input" value={formCliente.identificacion}
                onChange={(e) => setFormCliente({ ...formCliente, identificacion: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); crearCliente(); } }}
              />
            </div>
            <div className="field" style={{ gridColumn: '1/-1' }}>
              <label>Correo</label>
              <input
                className="input" type="email" value={formCliente.email}
                onChange={(e) => setFormCliente({ ...formCliente, email: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); crearCliente(); } }}
              />
            </div>
          </div>
          <div className="flex gap-sm" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setFormCliente(null)}>Cancelar</button>
            <button
              className="btn btn-primary btn-sm"
              onClick={crearCliente}
              disabled={!formCliente.nombre.trim() || creandoCliente}
            >
              {creandoCliente ? 'Creando...' : 'Crear y usar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const camposEquipo = (
    <>
      <div className="field">
        <label>Dispositivo *</label>
        <input className="input" value={form.dispositivo || ''} placeholder="Marca, modelo y color"
          onChange={(e) => setForm({ ...form, dispositivo: e.target.value })} />
      </div>
      <div className="field">
        <label>Descripcion de la falla</label>
        <textarea className="input" rows={2} value={form.descripcion || ''}
          onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
      </div>
      <div className="grid-2" style={{ gap: 12 }}>
        <div className="field">
          <label>Entrega estimada</label>
          <input className="input" type="date" value={form.fecha_entrega_estimada || ''}
            onChange={(e) => setForm({ ...form, fecha_entrega_estimada: e.target.value })} />
        </div>
        <div className="field">
          <label>Presupuesto (₡)</label>
          <input className="input" type="number" min="0" value={form.total ?? ''}
            onChange={(e) => setForm({ ...form, total: e.target.value })} />
        </div>
      </div>
      <p className="muted" style={{ fontSize: 11, marginTop: -6, marginBottom: 12, fontStyle: 'italic' }}>
        {LEYENDA_IVA_INCLUIDO}
      </p>
      <div className="field">
        <label>Notas internas</label>
        <textarea className="input" rows={2} value={form.notas || ''}
          onChange={(e) => setForm({ ...form, notas: e.target.value })} />
      </div>
    </>
  );

  const selectorFotos = (
    <input
      ref={archivoRef} type="file" accept="image/png,image/jpeg" multiple
      style={{ display: 'none' }}
      onChange={(e) => elegirFotos(e.target.files)}
    />
  );

  const botonAgregarFoto = (
    <button
      type="button" className="btn btn-outline" disabled={subiendo}
      onClick={() => archivoRef.current?.click()}
      style={{ aspectRatio: '1 / 1', flexDirection: 'column', gap: 4, fontSize: 12 }}
    >
      <Icon.plus width={22} height={22} />
      {subiendo ? 'Subiendo…' : 'Agregar foto'}
    </button>
  );

  return (
    <div>
      <PageHeader title="Reparaciones" subtitle="Taller de reparacion de celulares y dispositivos">
        <button className="btn btn-primary" onClick={abrirNuevo}>
          <Icon.plus /> Nueva reparacion
        </button>
      </PageHeader>

      {/* Lo que hay que cobrar, primero. Era lo que no se veia por ningun lado. */}
      {Array.isArray(reparaciones) && (
        <div style={{
          // 150px: tres columnas en escritorio y dos en telefono, en vez de
          // tres tarjetas apiladas que empujaban la lista fuera de la vista.
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12, marginBottom: 16,
        }}>
          <div className="card" style={{ padding: 16, borderColor: resumen.porCobrar > 0 ? 'var(--danger)' : 'var(--border)' }}>
            <div className="muted" style={{ fontSize: 13 }}>Por cobrar</div>
            <div className="mono" style={{
              fontSize: 24, fontWeight: 800, fontFamily: 'var(--font-display)',
              color: resumen.porCobrar > 0 ? 'var(--danger)' : 'var(--success)',
            }}>
              {crc(resumen.porCobrar)}
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              {resumen.conSaldo ? `${resumen.conSaldo} reparacion(es) con saldo` : 'Todo cobrado'}
            </div>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div className="muted" style={{ fontSize: 13 }}>En el taller</div>
            <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--font-display)' }}>{resumen.abiertas}</div>
            <div className="muted" style={{ fontSize: 12 }}>sin entregar</div>
          </div>
          <div className="card" style={{ padding: 16 }}>
            <div className="muted" style={{ fontSize: 13 }}>Listas para entregar</div>
            <div style={{ fontSize: 24, fontWeight: 800, fontFamily: 'var(--font-display)', color: 'var(--success)' }}>
              {resumen.listas}
            </div>
            <div className="muted" style={{ fontSize: 12 }}>avisar al cliente</div>
          </div>
        </div>
      )}

      <FilterBar
        desde={desde} hasta={hasta} onDesde={setDesde} onHasta={setHasta}
        showBusqueda busqueda={q} onBusqueda={setQ}
        placeholderBusqueda="Numero, dispositivo o cliente…"
        onLimpiar={limpiarFiltros}
        resumen={Array.isArray(reparaciones)
          ? `${reparaciones.length} reparacion(es) · filtra por fecha de entrada al taller`
          : ''}
      >
        <div className="field" style={{ margin: 0 }}>
          <label>Estado</label>
          <select className="select" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
            <option value="">Todos</option>
            {ESTADOS.map((e) => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
        </div>
      </FilterBar>

      {errorLista && (
        <div className="card" style={{ padding: 14, marginBottom: 16, borderColor: 'var(--danger)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Icon.alert width={18} height={18} style={{ color: 'var(--danger)', flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 14 }}>{errorLista}</span>
          <button className="btn btn-outline btn-sm" onClick={cargar}>Reintentar</button>
        </div>
      )}

      {reparaciones === undefined ? (
        <Loading />
      ) : reparaciones === null ? null : reparaciones.length === 0 ? (
        <EmptyState icon="reparaciones" titulo="Sin reparaciones" texto="Registra el primer equipo que entra al taller." />
      ) : (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Numero</th>
                <th className="col-sm"></th>
                <th>Dispositivo</th>
                <th>Cliente</th>
                <th>Estado</th>
                <th className="col-md">Pago</th>
                <th className="col-sm">Entrada</th>
                <th className="col-md">Entrega est.</th>
                <th className="text-right">Total</th>
                <th className="text-right">Por cobrar</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reparaciones.map((r) => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => abrirEditar(r)}>
                  <td className="mono">{r.numero}</td>
                  <td className="col-sm">
                    {r.foto_id ? (
                      <ImagenProtegida
                        ruta={`/reparaciones/${r.id}/imagenes/${r.foto_id}`}
                        alt=""
                        style={{
                          width: 44, height: 44, objectFit: 'cover', display: 'block',
                          borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                        }}
                      />
                    ) : (
                      <div style={{
                        width: 44, height: 44, borderRadius: 'var(--radius-sm)',
                        background: 'var(--surface-2)', border: '1px dashed var(--border)',
                      }} />
                    )}
                  </td>
                  <td style={{ fontWeight: 600 }}>{r.dispositivo}</td>
                  <td className="muted">{r.cliente_nombre || 'Sin cliente'}</td>
                  {/* El cambio de estado no abre el detalle: se detiene el clic. */}
                  <td onClick={(e) => e.stopPropagation()}>
                    <select
                      className="select"
                      value={r.estado}
                      onChange={(e) => cambiarEstado(r, e.target.value)}
                      style={{
                        width: 'auto', border: 'none', padding: '4px 10px', fontSize: 12, fontWeight: 600,
                        borderRadius: 999, cursor: 'pointer',
                        background: ESTADO_INFO[r.estado]?.bg,
                        color: ESTADO_INFO[r.estado]?.color,
                      }}
                    >
                      {ESTADOS.map((e) => (
                        <option key={e.value} value={e.value}>{e.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="col-md"><PagoBadge reparacion={r} /></td>
                  <td className="muted col-sm">{fecha(r.fecha_entrada)}</td>
                  <td className="muted col-md">{r.fecha_entrega_estimada ? fecha(r.fecha_entrega_estimada) : '—'}</td>
                  <td className="text-right mono" style={{ fontWeight: 600 }}>{crc(totalReal(r))}</td>
                  <td className="text-right mono" style={{ fontWeight: 700, color: pendiente(r) > 0 ? 'var(--danger)' : 'var(--success)' }}>
                    {pendiente(r) > 0 ? crc(pendiente(r)) : 'Pagado'}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => compartir(r)} title="Copiar link publico">
                        <Icon.share width={15} height={15} />
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setTiquete(r)} title="Tiquete de respaldo">
                        <Icon.print width={15} height={15} />
                      </button>
                      <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => eliminar(r)} title="Eliminar">
                        <Icon.trash width={15} height={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editando ? `Reparacion ${editando.numero || ''}` : 'Nueva reparacion'}
        width={editando ? 780 : 640}
      >
        {selectorFotos}

        {!editando ? (
          /* ---------------- Alta ---------------- */
          <>
            {campoCliente}
            {camposEquipo}

            <Seccion titulo="Fotos del equipo">
              <div style={MOSAICO}>
                {pendientes.map((archivo, i) => (
                  <div key={i} style={{ position: 'relative', aspectRatio: '1 / 1' }}>
                    <img
                      src={URL.createObjectURL(archivo)} alt=""
                      style={{
                        width: '100%', height: '100%', objectFit: 'cover',
                        borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                      }}
                    />
                    <button type="button" className="btn btn-sm" title="Quitar"
                      onClick={() => setPendientes((prev) => prev.filter((_, x) => x !== i))}
                      style={BOTON_BORRAR_FOTO}>
                      <Icon.close width={13} height={13} />
                    </button>
                  </div>
                ))}
                {botonAgregarFoto}
              </div>
              {pendientes.length > 0 && (
                <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                  Se suben al guardar la reparacion.
                </p>
              )}
            </Seccion>

            <Seccion titulo="Abono al recibir el equipo">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input className="input" type="number" min="0" step="0.01"
                  value={form.abono ?? ''} style={{ flex: '1 1 140px' }}
                  aria-label="Monto del abono inicial"
                  onChange={(e) => setForm({ ...form, abono: e.target.value })} />
                <select className="select" value={form.metodo_abono || 'efectivo'} style={{ flex: '0 0 auto', width: 'auto' }}
                  aria-label="Metodo del abono inicial"
                  onChange={(e) => setForm({ ...form, metodo_abono: e.target.value })}>
                  {METODOS_PAGO.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                Opcional. Entra a la caja abierta con el metodo que se elija.
                {Number(form.abono) > 0 && Number(form.total) > 0 && (
                  <> Quedan <strong>{crc(Math.max(Number(form.total) - Number(form.abono), 0))}</strong> por cobrar.</>
                )}
              </p>
            </Seccion>
          </>
        ) : (
          /* ---------------- Detalle ---------------- */
          <>
            <div className="flex items-center justify-between" style={{ flexWrap: 'wrap', gap: 8 }}>
              <span className="flex items-center gap-sm">
                <EstadoBadge estado={editando.estado} />
                <PagoBadge reparacion={editando} />
              </span>
              <span className="muted" style={{ fontSize: 12 }}>
                Entro {fecha(editando.fecha_entrada)}
                {editando.fecha_entrega && ` · Entregado ${fecha(editando.fecha_entrega)}`}
              </span>
            </div>

            {/* La cuenta, arriba y en grande: es lo primero que se pregunta
                cuando el cliente viene a retirar. */}
            <div style={{
              marginTop: 16, borderRadius: 'var(--radius)', overflow: 'hidden',
              border: `1px solid ${pendiente(editando) > 0 ? 'var(--danger)' : 'var(--success)'}`,
            }}>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 12, padding: 14, background: 'var(--surface-2)',
              }}>
                <Cifra etiqueta="Presupuesto" valor={Number(editando.total || 0)} />
                <Cifra etiqueta="Extras" valor={sumaExtras(editando)} />
                <Cifra etiqueta="Total" valor={totalReal(editando)} fuerte />
                <Cifra etiqueta="Abonado" valor={Number(editando.abono || 0)} color="var(--success)" />
              </div>
              <div className="flex items-center justify-between" style={{
                padding: '12px 14px', gap: 8, flexWrap: 'wrap',
                background: pendiente(editando) > 0 ? 'var(--danger-bg)' : 'var(--success-bg)',
              }}>
                <span style={{ fontWeight: 700, color: pendiente(editando) > 0 ? 'var(--danger)' : 'var(--success)' }}>
                  {pendiente(editando) > 0 ? 'Falta cobrar' : 'Pagada por completo'}
                </span>
                <span className="mono" style={{
                  fontSize: 22, fontWeight: 800,
                  color: pendiente(editando) > 0 ? 'var(--danger)' : 'var(--success)',
                }}>
                  {crc(pendiente(editando))}
                </span>
              </div>
            </div>

            <Seccion titulo="Abonos">
              {cargandoEditar ? (
                <Loading texto="Cargando abonos..." />
              ) : (
                <>
                  {pendiente(editando) > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <input
                        className="input" type="number" min="0.01" step="0.01"
                        style={{ flex: '1 1 130px' }} aria-label="Monto del abono"
                        value={nuevoAbono.monto}
                        onChange={(e) => setNuevoAbono({ ...nuevoAbono, monto: e.target.value })}
                      />
                      <select className="select" style={{ flex: '0 0 auto', width: 'auto' }} aria-label="Metodo del abono"
                        value={nuevoAbono.metodo}
                        onChange={(e) => setNuevoAbono({ ...nuevoAbono, metodo: e.target.value })}>
                        {METODOS_PAGO.map((m) => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                      <input className="input" style={{ flex: '2 1 160px' }} aria-label="Nota del abono"
                        placeholder="Nota (opcional)"
                        value={nuevoAbono.nota}
                        onChange={(e) => setNuevoAbono({ ...nuevoAbono, nota: e.target.value })} />
                      <button type="button" className="btn btn-primary" onClick={agregarAbono} disabled={agregandoAbono}>
                        {agregandoAbono ? <span className="spinner" /> : 'Registrar abono'}
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm"
                        onClick={() => setNuevoAbono({ ...nuevoAbono, monto: pendiente(editando).toFixed(2) })}>
                        Cobrar todo ({crc(pendiente(editando))})
                      </button>
                    </div>
                  )}

                  {(editando.abonos || []).length === 0 ? (
                    <p className="muted" style={{ fontSize: 13 }}>Todavia no hay abonos registrados.</p>
                  ) : (
                    <div className="table-wrap">
                      <table className="data">
                        <thead>
                          <tr><th>Fecha</th><th>Metodo</th><th>Nota</th><th className="text-right">Monto</th><th /></tr>
                        </thead>
                        <tbody>
                          {editando.abonos.map((ab) => (
                            <tr key={ab.id}>
                              <td className="muted">{fechaHora(ab.created_at)}</td>
                              <td style={{ textTransform: 'capitalize' }}>{ab.metodo}</td>
                              <td className="muted">{ab.nota || '—'}</td>
                              <td className="text-right mono" style={{ fontWeight: 600 }}>{crc(ab.monto)}</td>
                              <td className="text-right">
                                <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                                  onClick={() => eliminarAbono(ab.id)} title="Eliminar abono">
                                  <Icon.trash width={14} height={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </Seccion>

            <Seccion titulo="Fotos del equipo">
              {cargandoEditar ? (
                <Loading texto="Cargando fotos..." />
              ) : (
                <div style={MOSAICO}>
                  {(editando.imagenes || []).map((img) => (
                    <div key={img.id} style={{ position: 'relative', aspectRatio: '1 / 1' }}>
                      <ImagenProtegida
                        ruta={`/reparaciones/${editando.id}/imagenes/${img.id}`}
                        preview={img.preview || null}
                        alt={img.nombre_original || 'Foto del equipo'}
                        onClick={() => setVerFoto(`/reparaciones/${editando.id}/imagenes/${img.id}`)}
                        style={{
                          width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in',
                          borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                        }}
                      />
                      <button type="button" className="btn btn-sm" title="Eliminar foto"
                        onClick={() => borrarFoto(img.id)} style={BOTON_BORRAR_FOTO}>
                        <Icon.close width={13} height={13} />
                      </button>
                    </div>
                  ))}
                  {botonAgregarFoto}
                </div>
              )}
            </Seccion>

            <Seccion titulo="Extras fuera del presupuesto">
              {cargandoEditar ? (
                <Loading texto="Cargando extras..." />
              ) : (
                <>
                  {(editando.extras || []).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                      {editando.extras.map((ex) => (
                        <div key={ex.id} className="flex items-center justify-between" style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--surface-2)' }}>
                          <span>{ex.descripcion}</span>
                          <span className="flex items-center gap-sm">
                            <span className="mono">{crc(ex.monto)}</span>
                            <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => eliminarExtra(ex.id)}>
                              <Icon.trash width={14} height={14} />
                            </button>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input className="input" style={{ flex: '2 1 180px' }} aria-label="Descripcion del extra"
                      placeholder="Que hubo que comprar"
                      value={nuevoExtra.descripcion}
                      onChange={(e) => setNuevoExtra({ ...nuevoExtra, descripcion: e.target.value })} />
                    <input className="input" type="number" style={{ flex: '1 1 110px' }} aria-label="Monto del extra"
                      value={nuevoExtra.monto}
                      onChange={(e) => setNuevoExtra({ ...nuevoExtra, monto: e.target.value })} />
                    <button type="button" className="btn btn-outline" onClick={agregarExtra} disabled={agregandoExtra}>
                      <Icon.plus width={16} height={16} /> Agregar
                    </button>
                  </div>
                </>
              )}
            </Seccion>

            <Seccion titulo="Datos del equipo y del cliente">
              {campoCliente}
              {camposEquipo}
            </Seccion>
          </>
        )}

        <div className="flex gap-sm" style={{ justifyContent: 'flex-end', marginTop: 18, flexWrap: 'wrap' }}>
          {editando && (
            <>
              <button className="btn btn-outline" onClick={() => compartir(editando)}>
                <Icon.share width={15} height={15} /> Link
              </button>
              <button className="btn btn-outline" onClick={() => setTiquete(editando)}>
                <Icon.print width={15} height={15} /> Tiquete
              </button>
            </>
          )}
          <button className="btn btn-outline" onClick={() => setModal(false)}>{editando ? 'Cerrar' : 'Cancelar'}</button>
          <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
            {guardando ? <span className="spinner" /> : editando ? 'Guardar datos' : 'Registrar reparacion'}
          </button>
        </div>
      </Modal>

      <Modal open={!!tiquete} onClose={() => setTiquete(null)} title={tiquete ? `Tiquete ${tiquete.numero}` : 'Tiquete'} width={380}>
        {tiquete && <TiqueteReparacion reparacion={tiquete} />}
      </Modal>

      {/* Ver una foto en grande */}
      <Modal open={!!verFoto} onClose={() => setVerFoto(null)} title="Foto del equipo" width={900}>
        {verFoto && (
          <ImagenProtegida ruta={verFoto} alt="Foto del equipo"
            style={{ width: '100%', minHeight: 240, maxHeight: '75vh', objectFit: 'contain', borderRadius: 'var(--radius-sm)' }} />
        )}
      </Modal>
    </div>
  );
}
